import { type ReactNode, useState } from "react"
import { IconDeviceFloppy, IconLoader2, IconRefresh, IconToggleRight } from "@tabler/icons-react"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { useAppDispatch, useAppSelector } from "@/store"
import { hasPermission } from "@/store/permissionsSlice"
import type { FundingProgramState } from "../booking-season/bookingSeasonApi"
import { formatSeasonDateTime } from "../booking-season/bookingSeasonFormat"
import {
  type FundingSwitchesResponse,
  type FundingSwitchHistoryEntry,
  fundingSwitchesApi,
  useGetFundingSwitchesQuery,
  useSaveFundingSwitchMutation,
} from "./fundingSwitchesApi"
import {
  buildSaveRequest,
  defaultMessage,
  isSaveRefusal,
  MESSAGE_MAX_LENGTH,
  programLabel,
  rowFromState,
  sameRow,
  type SwitchRowState,
  validateRow,
} from "./lib/fundingSwitchForm"

/**
 * The funding switches (AB#607): per program, whether the public website and phone line may book it,
 * and what the public is told while it is off. Every save is kept, with who made it, as the audit.
 */
export default function FundingSwitchesPage() {
  const dispatch = useAppDispatch()
  const { permissions } = useAppSelector((state) => state.permissions)
  // The same people who manage the booking season, which the switches sit beside.
  const canManage = hasPermission(permissions, "appointments:manage-booking-season")

  const { data, isLoading, isFetching, error, refetch } = useGetFundingSwitchesQuery(undefined, { skip: !canManage })

  // Show what a save answered with straight away, rather than the old switches until the refetch lands.
  function followSave(next: FundingSwitchesResponse) {
    dispatch(fundingSwitchesApi.util.upsertQueryData("getFundingSwitches", undefined, next))
  }

  if (!canManage) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        <Header />
        <p className="text-muted-foreground">You do not have permission to change the funding switches.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <Header>
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          {isFetching ? <IconLoader2 className="size-4 animate-spin" /> : <IconRefresh className="size-4" />}
          Refresh
        </Button>
      </Header>

      <p className="text-muted-foreground max-w-3xl">
        Which funding programs the public website and phone line can book. A program that is off is not offered, and
        the public is told its message instead. With the three booking programs off (LIHEAP, PSE HELP gas and PSE
        HELP electric), public booking is closed as if the booking season had ended; SHEAP is never booked, so its
        switch does not close booking. Staff booking is never affected. Changes apply at once.
      </p>

      {isLoading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : error || !data ? (
        <div className="flex flex-col items-start gap-2 rounded-xl border bg-card p-6">
          <p className="text-sm text-destructive">
            {(error as { status?: number } | undefined)?.status === 403
              ? "You do not have permission to view the funding switches."
              : "Could not load the funding switches."}
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            {data.programs.map((state) => (
              // Keyed on the saved state too, so a save (or another admin's) resets the row's edits.
              <SwitchRow key={`${state.program}:${state.createdAt ?? ""}`} state={state} onSaved={followSave} />
            ))}
          </div>
          <SwitchHistory history={data.history} />
        </>
      )}
    </div>
  )
}

interface SwitchRowProps {
  state: FundingProgramState
  onSaved: (next: FundingSwitchesResponse) => void
}

/** One program: its switch, its message, and Save. */
export function SwitchRow({ state, onSaved }: SwitchRowProps) {
  const saved = rowFromState(state)
  const [row, setRow] = useState<SwitchRowState>(saved)
  const [serverErrors, setServerErrors] = useState<string[]>([])
  const [save, { isLoading: saving }] = useSaveFundingSwitchMutation()

  const label = programLabel(state.program)
  const issue = validateRow(row)
  const unchanged = sameRow(row, saved)
  const request = buildSaveRequest(state.program, row)
  const id = `funding-switch-${state.program}`

  const update = (patch: Partial<SwitchRowState>) => {
    setServerErrors([])
    setRow({ ...row, ...patch })
  }

  async function handleSave() {
    if (!request) return
    setServerErrors([])
    try {
      const next = await save(request).unwrap()
      onSaved(next)
      toast.success(`${label} saved.`, {
        description: request.isEnabled ? `${label} can be booked.` : `${label} is no longer offered to the public.`,
      })
    } catch (err) {
      const error = err as { status?: number; data?: unknown }
      if (error.status === 400 && isSaveRefusal(error.data)) {
        setServerErrors(error.data.errors)
      } else if (error.status === 403) {
        setServerErrors(["You do not have permission to change the funding switches."])
      } else {
        setServerErrors(["Could not save the switch. Try again in a moment."])
      }
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{label}</CardTitle>
        <CardDescription>
          {state.createdBy && state.createdAt
            ? `Last changed by ${state.createdBy}, ${formatSeasonDateTime(state.createdAt)}.`
            : "Never changed: on, with the default message."}
        </CardDescription>
        <CardAction>
          <Badge variant={state.isEnabled ? "default" : "destructive"}>{state.isEnabled ? "On" : "Off"}</Badge>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${id}-enabled`}
            checked={row.isEnabled}
            onCheckedChange={(checked) => update({ isEnabled: checked === true })}
          />
          <Label htmlFor={`${id}-enabled`}>Open for booking</Label>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={`${id}-message`}>Message while off</Label>
          <Textarea
            id={`${id}-message`}
            value={row.message}
            placeholder={defaultMessage(state.program)}
            aria-invalid={issue ? true : undefined}
            onChange={(event) => update({ message: event.target.value })}
            rows={3}
          />
          <p className="text-xs text-muted-foreground">
            Shown on the website and read to phone callers as written. Blank uses the default: “
            {defaultMessage(state.program)}” {row.message.trim().length}/{MESSAGE_MAX_LENGTH}
          </p>
          {issue && <p className="text-sm text-destructive">{issue}</p>}
          {serverErrors.map((message) => (
            <p key={message} className="text-sm text-destructive">
              {message}
            </p>
          ))}
        </div>
      </CardContent>
      <CardFooter className="flex justify-end gap-2">
        <Button variant="outline" size="sm" disabled={unchanged || saving} onClick={() => setRow(saved)}>
          Discard
        </Button>
        <Button size="sm" disabled={unchanged || !request || saving} onClick={() => void handleSave()}>
          {saving ? <IconLoader2 className="size-4 animate-spin" /> : <IconDeviceFloppy className="size-4" />}
          Save
        </Button>
      </CardFooter>
    </Card>
  )
}

/** The newest saved switches, newest first: the audit trail. */
export function SwitchHistory({ history }: { history: FundingSwitchHistoryEntry[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>History</CardTitle>
        <CardDescription>The most recent changes, newest first. All times are Pacific.</CardDescription>
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground">No switch has been changed yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Program</TableHead>
                <TableHead>Switch</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((entry, index) => (
                <TableRow key={`${entry.createdAt}-${entry.program}-${index}`}>
                  <TableCell className="whitespace-nowrap">{formatSeasonDateTime(entry.createdAt)}</TableCell>
                  <TableCell>{programLabel(entry.program)}</TableCell>
                  <TableCell>{entry.isEnabled ? "On" : "Off"}</TableCell>
                  <TableCell className="whitespace-normal">{entry.message ?? "Default message"}</TableCell>
                  <TableCell>{entry.createdBy}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

function Header({ children }: { children?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <IconToggleRight className="size-6" />
        <h1 className="text-2xl font-bold">Funding Switches</h1>
      </div>
      {children}
    </div>
  )
}
